import { resolveNodeField } from '../../../core/assistant/utils/conditionEvaluation';
import { fieldRegistry } from '../../../core/fields/registry/FieldRegistry';
import { getOrgChartPersonFieldPacks, getOrgChartPositionFieldPacks } from '../fields/fieldPackToManifest';
import type {
  OrgCustomField,
  OrgCustomFieldDefinition,
  OrgCustomFieldOverrideMap,
} from '../models/customFields';
import { resolveEffectiveCustomFields } from '../services/orgCustomFieldResolver';

export interface OrgCardFieldOption {
  id: string;
  label: string;
  source: 'ontology' | 'custom' | 'computed';
}

type CardFieldTranslate = (key: string, opts?: { defaultValue?: string }) => unknown;

/** Localized label for card field picker / visibility toggles; custom fields keep user-defined names. */
export function translateOrgCardFieldOptionLabel(tr: CardFieldTranslate, option: OrgCardFieldOption): string {
  if (option.source === 'custom') return option.label;
  return tr(`orgchart.cardFields.${option.id}`, { defaultValue: option.label }) as string;
}

export interface OrgCardDetailRow {
  id: string;
  label: string;
  value: string;
}

export interface OrgCardManagerInfo {
  name?: string;
  positionTitle?: string;
}

export const DEFAULT_PERSON_CARD_VISIBLE_FIELD_IDS = new Set<string>([
  'personId',
  'department',
  'email',
  'manager',
]);

export const DEFAULT_POSITION_CARD_VISIBLE_FIELD_IDS = new Set<string>([
  'plannedFillBy',
]);

const EXCLUDED_DETAIL_FIELD_IDS = new Set<string>([
  'name',
  'firstName',
  'lastName',
  'preferredName',
  'title',
  'role',
  'photoRef',
  'reportsTo',
  'managerId',
  'customFields',
  'visibleFields',
  'dataBinding',
]);

const COMPUTED_CARD_FIELDS: OrgCardFieldOption[] = [
  { id: 'manager', label: 'Manager', source: 'computed' },
];
const ORG_CUSTOM_FIELD_OVERRIDES_KEY = 'customFieldOverrides';

let PERSON_ONTOLOGY_FIELDS_CACHE: OrgCardFieldOption[] | null = null;
let POSITION_ONTOLOGY_FIELDS_CACHE: OrgCardFieldOption[] | null = null;

const toDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.filter(Boolean).map((v) => String(v)).join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value).trim();
};

/** Localized Yes/No and same formatting as {@link toDisplayValue} for card detail text. */
export function formatOrgCardFieldDisplayValue(tr: CardFieldTranslate, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.filter(Boolean).map((v) => String(v)).join(', ');
  if (typeof value === 'boolean') {
    return value
      ? (tr('orgchart.cardFieldDisplay.yes', { defaultValue: 'Yes' }) as string)
      : (tr('orgchart.cardFieldDisplay.no', { defaultValue: 'No' }) as string);
  }
  return String(value).trim();
}

export const hasDisplayValue = (value: unknown): boolean => toDisplayValue(value).length > 0;

const normalizeName = (value: string): string => value.trim().toLowerCase();

function readOverrideMap(value: unknown): OrgCustomFieldOverrideMap {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: OrgCustomFieldOverrideMap = {};
  Object.entries(value as Record<string, unknown>).forEach(([key, entry]) => {
    if (typeof entry === 'string' || typeof entry === 'number') {
      out[key] = entry;
    }
  });
  return out;
}

export const resolveNodeCustomFields = ({
  nodeType,
  attrs,
  props,
  definitions,
}: {
  nodeType: string;
  attrs: Record<string, unknown>;
  props: Record<string, unknown>;
  definitions: OrgCustomFieldDefinition[];
}): OrgCustomField[] => {
  const legacyCustomFields = ((attrs.customFields as OrgCustomField[]) || (props.customFields as OrgCustomField[]) || [])
    .filter((field): field is OrgCustomField => !!field && typeof field.name === 'string');
  const overrides = readOverrideMap(attrs[ORG_CUSTOM_FIELD_OVERRIDES_KEY] ?? props[ORG_CUSTOM_FIELD_OVERRIDES_KEY]);

  const resolved = resolveEffectiveCustomFields({
    nodeType,
    definitions,
    overrides,
  }).fields;
  if (resolved.length === 0) return legacyCustomFields;

  const legacyById = new Map<string, OrgCustomField>();
  const legacyByName = new Map<string, OrgCustomField>();
  legacyCustomFields.forEach((field) => {
    const id = String(field.id ?? '').trim();
    const name = String(field.name ?? '').trim();
    if (id) legacyById.set(id, field);
    if (name) legacyByName.set(normalizeName(name), field);
  });

  const matchedLegacy = new Set<OrgCustomField>();
  const resolvedCustomFields: OrgCustomField[] = resolved.map((field) => {
    const legacyMatch = legacyById.get(field.fieldId) ?? legacyByName.get(normalizeName(field.name));
    if (legacyMatch) matchedLegacy.add(legacyMatch);
    return {
      id: field.fieldId,
      name: field.name,
      type: field.type,
      ...(field.value !== undefined
        ? { value: field.value }
        : (legacyMatch?.value !== undefined ? { value: legacyMatch.value } : {})),
      ...(field.showOnCard !== undefined
        ? { showOnCard: field.showOnCard }
        : (legacyMatch?.showOnCard !== undefined ? { showOnCard: legacyMatch.showOnCard } : {})),
    };
  });

  const unmatchedLegacy = legacyCustomFields.filter((field) => !matchedLegacy.has(field));
  return [...resolvedCustomFields, ...unmatchedLegacy];
};

export const getPersonOntologyCardFieldOptions = (): OrgCardFieldOption[] => {
  if (PERSON_ONTOLOGY_FIELDS_CACHE) return PERSON_ONTOLOGY_FIELDS_CACHE;

  const fields = fieldRegistry
    .getFieldsFromPacks(getOrgChartPersonFieldPacks())
    .filter((field) => !EXCLUDED_DETAIL_FIELD_IDS.has(field.id))
    .map((field) => ({
      id: field.id,
      label: field.label,
      source: 'ontology' as const,
    }));

  const unique = new Map<string, OrgCardFieldOption>();
  for (const field of fields) {
    if (!unique.has(field.id)) unique.set(field.id, field);
  }
  for (const computed of COMPUTED_CARD_FIELDS) {
    if (!unique.has(computed.id)) unique.set(computed.id, computed);
  }

  PERSON_ONTOLOGY_FIELDS_CACHE = Array.from(unique.values());
  return PERSON_ONTOLOGY_FIELDS_CACHE;
};

export const getPositionOntologyCardFieldOptions = (): OrgCardFieldOption[] => {
  if (POSITION_ONTOLOGY_FIELDS_CACHE) return POSITION_ONTOLOGY_FIELDS_CACHE;

  const fields = fieldRegistry
    .getFieldsFromPacks(getOrgChartPositionFieldPacks())
    .filter((field) => !EXCLUDED_DETAIL_FIELD_IDS.has(field.id))
    .map((field) => ({
      id: field.id,
      label: field.label,
      source: 'ontology' as const,
    }));

  const unique = new Map<string, OrgCardFieldOption>();
  for (const field of fields) {
    if (!unique.has(field.id)) unique.set(field.id, field);
  }

  POSITION_ONTOLOGY_FIELDS_CACHE = Array.from(unique.values());
  return POSITION_ONTOLOGY_FIELDS_CACHE;
};

export const getCustomCardFieldOptions = (
  customFields: OrgCustomField[],
): OrgCardFieldOption[] => {
  const options: OrgCardFieldOption[] = [];
  const seen = new Set<string>();
  for (const field of customFields) {
    if (!field?.name) continue;
    const id = `custom_${String(field.id ?? field.name).trim()}`;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    options.push({
      id,
      label: field.name,
      source: 'custom',
    });
  }
  return options;
};

export const mergeCardFieldOptions = (
  ontology: OrgCardFieldOption[],
  custom: OrgCardFieldOption[],
): OrgCardFieldOption[] => {
  const unique = new Map<string, OrgCardFieldOption>();
  for (const option of ontology) unique.set(option.id, option);
  for (const option of custom) unique.set(option.id, option);
  return Array.from(unique.values());
};

export const buildVisibleCardFieldMap = (
  visibleFields: Record<string, boolean> | undefined,
  allOptions: OrgCardFieldOption[],
  defaultVisibleFieldIds: Set<string> = DEFAULT_PERSON_CARD_VISIBLE_FIELD_IDS,
): Record<string, boolean> => {
  const map: Record<string, boolean> = {};
  const input = visibleFields ?? {};
  for (const option of allOptions) {
    if (Object.prototype.hasOwnProperty.call(input, option.id)) {
      map[option.id] = !!input[option.id];
      continue;
    }
    if (option.source === 'custom') {
      map[option.id] = true;
      continue;
    }
    map[option.id] = defaultVisibleFieldIds.has(option.id);
  }
  return map;
};

const resolveCustomFieldValue = (
  fieldId: string,
  customFields: OrgCustomField[],
): unknown => {
  const customId = fieldId.replace(/^custom_/, '');
  const byId = customFields.find((f) => String(f.id ?? '').trim() === customId);
  if (byId) return byId.value;
  const byName = customFields.find((f) => String(f.name ?? '').trim() === customId);
  return byName?.value;
};

const resolveCardFieldValue = (
  fieldId: string,
  attrs: Record<string, unknown>,
  props: Record<string, unknown>,
  nodeName: string,
  customFields: OrgCustomField[],
  managerInfo: OrgCardManagerInfo | null,
): unknown => {
  if (fieldId === 'manager') {
    return managerInfo?.name || managerInfo?.positionTitle || '';
  }
  if (fieldId.startsWith('custom_')) {
    return resolveCustomFieldValue(fieldId, customFields);
  }
  return resolveNodeField({ id: 'node', name: nodeName, attrs, props }, fieldId);
};

export const resolvePersonCardFieldValue = ({
  fieldId,
  attrs,
  props,
  nodeName,
  customFields,
  managerInfo,
}: {
  fieldId: string;
  attrs: Record<string, unknown>;
  props: Record<string, unknown>;
  nodeName: string;
  customFields: OrgCustomField[];
  managerInfo: OrgCardManagerInfo | null;
}): unknown =>
  resolveCardFieldValue(fieldId, attrs, props, nodeName, customFields, managerInfo);

export const resolvePositionCardFieldValue = ({
  fieldId,
  attrs,
  props,
  nodeName,
  customFields,
}: {
  fieldId: string;
  attrs: Record<string, unknown>;
  props: Record<string, unknown>;
  nodeName: string;
  customFields: OrgCustomField[];
}): unknown =>
  resolveCardFieldValue(fieldId, attrs, props, nodeName, customFields, null);

export const buildPersonCardDetailRows = ({
  attrs,
  props,
  nodeName,
  customFields,
  allOptions,
  visibleMap,
  managerInfo,
  tr,
}: {
  attrs: Record<string, unknown>;
  props: Record<string, unknown>;
  nodeName: string;
  customFields: OrgCustomField[];
  allOptions: OrgCardFieldOption[];
  visibleMap: Record<string, boolean>;
  managerInfo: OrgCardManagerInfo | null;
  /** When set, row labels and boolean values are localized for the canvas card. */
  tr?: CardFieldTranslate;
}): OrgCardDetailRow[] => {
  const rows: OrgCardDetailRow[] = [];
  for (const option of allOptions) {
    if (!visibleMap[option.id]) continue;
    const raw = resolveCardFieldValue(
      option.id,
      attrs,
      props,
      nodeName,
      customFields,
      managerInfo,
    );
    const value = tr ? formatOrgCardFieldDisplayValue(tr, raw) : toDisplayValue(raw);
    if (!value) continue;
    rows.push({
      id: option.id,
      label: tr ? translateOrgCardFieldOptionLabel(tr, option) : option.label,
      value,
    });
  }
  return rows;
};
